import { Box, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { sxButtons, sxLabel, sxTextField, sxTitle, sxValidationMessage } from './sxProps';
import { cellphoneMask } from '../../../../../../utils/PhoneRegex';
import DefaultButton from '../../../../../../components/Accessibility/DefaultButton';
import { useAccessibility } from '../../../../../../Store/contexts/AccessibilityContext';
import { ModalDefault } from '../../../../../../components/ModalDefault';
import TypographyAccessibility from '../../../../../../components/Accessibility/TypographyAccessibility';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { UserData } from '..';

interface ContatoModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: UserData) => void;
  userData: UserData;
}

export const ContatoModal: React.FC<ContatoModalProps> = ({
  open,
  onClose,
  onSave,
  userData,
}) => {
  const accessibility = useAccessibility();

  const formik = useFormik({
    initialValues: userData,
    validationSchema: Yup.object({
      cel: Yup.string()
        .required('Celular é obrigatório')
        .matches(/^\(\d{2}\)\s\d{5}-\d{4}$/, 'Celular inválido ou incorreto'),
      tel: Yup.string()
        .required('Telefone é obrigatório')
        .matches(/^\(\d{2}\)\s\d{4}-\d{4}$/, 'Telefone inválido ou incorreto'),
      email: Yup.string().email('Insira um e-mail válido e correto').matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Insira um e-mail válido e correto').required('E-mail é obrigatório'),
    }),
    onSubmit: (values) => {
      onSave(values);
      onClose()
      formik.resetForm()
    },
  });

  useEffect(() => {
    formik.setValues(userData);
  }, [open]);

  return (
    <ModalDefault
      open={open}
      title="Atualizar dados do contato"
      sxTitle={sxTitle}
      onClose={onClose}
      maxWidth={578}
    >
      <Box style={{ ...accessibility.textStyleAccessibility, ...accessibility.contrastBorderAccessibility }}>
        <form onSubmit={formik.handleSubmit}>
          <div className="flex-col gap-20" style={{ marginBottom: '30px' }}>
            <div className="flex-col">
              <TypographyAccessibility component="label" sx={sxLabel}>
                Telefone
              </TypographyAccessibility>
              <TextField
                id="tel"
                variant="outlined"
                onChange={(e) => formik.setFieldValue('tel', cellphoneMask(e.target.value))}
                value={formik.values.tel}
                sx={sxTextField}
                fullWidth
                error={formik.touched.tel && Boolean(formik.errors.tel)}
                InputProps={{
                  style: { ...accessibility.contrastStyle },
                }}
                inputProps={{
                  maxLength: 14,
                  style: { color: accessibility.contrastMode ? 'white' : '' },
                }}
              />
              {formik.touched.tel && formik.errors.tel && (
                <div className="container-validation-message">
                  <FontAwesomeIcon icon={faCircleExclamation} size="sm" color="#D84141" />
                  <Typography
                    style={accessibility.textStyleAccessibility}
                    id="errorTel"
                    sx={sxValidationMessage}
                  >
                    {formik.errors.tel}
                  </Typography>
                </div>
              )}
            </div>

            <div className="flex-col">
              <TypographyAccessibility component="label" sx={sxLabel}>
                Celular
              </TypographyAccessibility>
              <TextField
                id="cel"
                variant="outlined"
                onChange={(e) => formik.setFieldValue('cel', cellphoneMask(e.target.value))}
                value={formik.values.cel}
                sx={sxTextField}
                fullWidth
                error={formik.touched.cel && Boolean(formik.errors.cel)}
                InputProps={{
                  style: { ...accessibility.contrastStyle },
                }}
                inputProps={{
                  maxLength: 15,
                  style: { color: accessibility.contrastMode ? 'white' : '' },
                }}
              />
              {formik.touched.cel && formik.errors.cel && (
                <div className="container-validation-message">
                  <FontAwesomeIcon icon={faCircleExclamation} size="sm" color="#D84141" />
                  <Typography
                    style={accessibility.textStyleAccessibility}
                    id="errorCel"
                    sx={sxValidationMessage}
                  >
                    {formik.errors.cel}
                  </Typography>
                </div>
              )}
            </div>

            <div className="flex-col">
              <TypographyAccessibility component="label" sx={sxLabel}>
                E-mail
              </TypographyAccessibility>
              <TextField
                id="email"
                variant="outlined"
                onChange={formik.handleChange}
                value={formik.values.email}
                sx={sxTextField}
                fullWidth
                error={formik.touched.email && Boolean(formik.errors.email)}
                InputProps={{
                  style: { ...accessibility.contrastStyle },
                }}
                inputProps={{
                  style: { color: accessibility.contrastMode ? 'white' : '' },
                }}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="container-validation-message">
                  <FontAwesomeIcon icon={faCircleExclamation} size="sm" color="#D84141" />
                  <Typography
                    style={accessibility.textStyleAccessibility}
                    id="errorEmail"
                    sx={sxValidationMessage}
                  >
                    {formik.errors.email}
                  </Typography>
                </div>
              )}
            </div>
          </div>

          <Box sx={sxButtons}>
            <DefaultButton text="Cancelar" variant="outlined" onClick={() => {
              formik.resetForm()
              onClose()
            }} />
            <DefaultButton text="Salvar" type="submit" onClick={formik.submitForm} />
          </Box>
        </form>
      </Box>
    </ModalDefault>
  );
};

export default ContatoModal;
